import type { CSSProperties } from 'react';
import { Select } from 'antd';
import {
  DOCUMENT_LANGUAGE_LABELS,
  SUPPORTED_DOCUMENT_LANGUAGES,
  type DocumentLanguage,
} from '../../hooks/useLanguage';
import { useDocumentLanguage } from '../../hooks/useDocumentLanguage';

export interface DocumentLanguageSelectorProps {
  /** Valoare controlată; dacă lipsește, se folosește starea hook-ului. */
  value?: DocumentLanguage;
  onChange?: (language: DocumentLanguage) => void;
  /** Textul etichetei vizibile (implicit: „Limba documentului"). */
  label?: string;
  id?: string;
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
  style?: CSSProperties;
  'aria-label'?: string;
}

const OPTIONS = SUPPORTED_DOCUMENT_LANGUAGES.map((code) => ({
  value: code,
  label: `${DOCUMENT_LANGUAGE_LABELS[code].flag} ${DOCUMENT_LANGUAGE_LABELS[code].native} (${DOCUMENT_LANGUAGE_LABELS[code].short})`,
}));

/**
 * Selector pentru limba DOCUMENTULUI — independent de limba meniului (§1.0c.7).
 *
 * - NU scrie în `localStorage['hepaskin-language']`;
 * - NU apelează `setLanguage` din `useLanguage`;
 * - oferă toate cele 3 limbi de document (RO / EN / SRB); SRB nu e ascuns,
 *   chiar dacă șablonul SRB poate lipsi (bannerul de fallback explică).
 */
const DocumentLanguageSelector = ({
  value,
  onChange,
  label = 'Limba documentului',
  id = 'document-language-select',
  size = 'middle',
  disabled = false,
  style,
  'aria-label': ariaLabel,
}: DocumentLanguageSelectorProps) => {
  const { language, setLanguage } = useDocumentLanguage();
  const current = value ?? language;

  const handleChange = (next: DocumentLanguage) => {
    if (onChange) {
      onChange(next);
      return;
    }
    setLanguage(next);
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
      <label htmlFor={id} style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
        {label}
      </label>
      <Select<DocumentLanguage>
        id={id}
        size={size}
        disabled={disabled}
        value={current}
        onChange={handleChange}
        options={OPTIONS}
        aria-label={ariaLabel ?? label}
        style={{ minWidth: 170 }}
      />
    </span>
  );
};

export default DocumentLanguageSelector;
